import { useState, useEffect } from 'react';
import { fetchPageContent } from '@/services/cms';
import { useCms } from '@/context/CmsContext';

/**
 * Fetches CMS content for a page (optionally filtered by section).
 * Returns a nested object: { [section]: { [field]: value } }
 *
 * Automatically refetches after a publish.
 */
export function useCmsContent(
    page: string,
    section?: string,
    fallbacks: Record<string, Record<string, string>> = {}
): {
    content: Record<string, Record<string, string>>;
    loading: boolean;
    error: Error | null;
    refetch: () => void;
} {
    const { publishVersion } = useCms();
    const [content, setContent] = useState<Record<string, Record<string, string>>>(fallbacks);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const [refreshKey, setRefreshKey] = useState(0);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);

        fetchPageContent(page, section)
            .then((data) => {
                if (cancelled) return;
                const merged = { ...fallbacks };
                for (const [sec, fields] of Object.entries(data)) {
                    merged[sec] = { ...(fallbacks[sec] || {}), ...fields };
                }
                setContent(merged);
                setError(null);
            })
            .catch((err) => {
                if (cancelled) return;
                setError(err);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => { cancelled = true; };
    }, [page, section, refreshKey, publishVersion]);

    const refetch = () => setRefreshKey((k) => k + 1);

    return { content, loading, error, refetch };
}

/**
 * Convenience helper: get a flat object for a single section.
 */
export function useCmsSection(
    page: string,
    section: string,
    fallbacks: Record<string, string> = {}
): {
    fields: Record<string, string>;
    loading: boolean;
    refetch: () => void;
} {
    const { content, loading, refetch } = useCmsContent(page, section, { [section]: fallbacks });
    return { fields: content[section] || fallbacks, loading, refetch };
}
